import {createStyles, Menu, MenuProps, Tooltip} from '@mantine/core';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/** Named widths matching the legacy LESS dropdown width variables. */
export enum DropdownMenuWidth {
  MINI = '128px',
  SMALL = '160px',
  MEDIUM = '192px',
  LARGE = '224px',
  XLARGE = '256px',
  XXLARGE = '294px',
}

export interface MantineDropdownMenuOption {
  name: string;
  icon?: BenchlingIconName;
  onSelect?: () => void;
  isDisabled?: boolean;
  /** Tooltip shown when the item is disabled. */
  disabledTooltip?: string;
  href?: string;
  openInNewTab?: boolean;
  /** Custom style variants */
  variant?: MantineDropdownMenuItemVariant;
  className?: string;
  dataTestAttrs?: DataTestAttrs;
}

export interface MantineDropdownMenuSection {
  /** Optional label shown at the top of the section. */
  label?: string;
  items: Array<MantineDropdownMenuOption>;
}

export interface MantineDropdownMenuProps {
  /** Width of the dropdown. Defaults to MEDIUM (192px). */
  width?: DropdownMenuWidth;
  sections: Array<MantineDropdownMenuSection>;
  /** The trigger element.
   *  Should render `<button>` under the hood. Other elements will not support `Space` and `Enter` key presses
   *  https://v6.mantine.dev/core/menu/#supported-target-elements */
  trigger: JSX.Element;
  /** Where the dropdown opens relative to the trigger. Defaults to 'bottom-end'. */
  position?: MenuProps['position'];
  /** Controlled open state. */
  opened?: boolean;
  /** Called when the menu opens or closes. */
  onOpenChange?: (opened: boolean) => void;
  /** Class applied to the dropdown container (Mantine Menu `dropdown` slot). */
  rootClassName?: string;
  /** Class applied to each menu item (Mantine Menu `item` slot). */
  itemClassName?: string;
  /** Class applied to each menu item's leading icon wrapper (Mantine Menu `itemIcon` slot). */
  itemIconClassName?: string;
}

/**
 * This component can be either controlled or uncontrolled depending on whether you pass `opened` / `onOpenChange`
 */
export default function MantineDropdownMenu({
  width = DropdownMenuWidth.MEDIUM,
  sections,
  trigger,
  position = 'bottom-end',
  opened,
  onOpenChange,
  rootClassName,
  itemClassName,
  itemIconClassName,
}: MantineDropdownMenuProps): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <Menu
      opened={opened}
      onChange={onOpenChange}
      position={position}
      withArrow={false}
      // Has to be defined like this in order to bypass Mantine's conversion from px to rem, which causes
      // sizing issues due to font resizing from LESS
      styles={{dropdown: {width: `${width} !important`}}}
      classNames={{
        dropdown: rootClassName,
        item: itemClassName,
        itemIcon: itemIconClassName,
      }}
    >
      <Menu.Target>{trigger}</Menu.Target>
      <Menu.Dropdown>
        {sections.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {sectionIndex > 0 && <Menu.Divider role="separator" />}
            {section.label && <Menu.Label>{section.label}</Menu.Label>}
            {section.items.map(
              (
                {
                  name,
                  icon,
                  onSelect,
                  isDisabled,
                  disabledTooltip,
                  href,
                  openInNewTab,
                  variant,
                  className,
                  dataTestAttrs,
                },
                itemIndex
              ) => {
                const itemIcon = icon ? <BenchlingIcon icon={icon} /> : undefined;
                const itemTestProps = dataTestAttrs ? getTestProps(dataTestAttrs) : {};
                const item = href ? (
                  <Menu.Item
                    key={itemIndex}
                    icon={itemIcon}
                    data-disabled={isDisabled || undefined}
                    aria-disabled={isDisabled}
                    component="a"
                    href={isDisabled ? undefined : href}
                    target={openInNewTab ? '_blank' : undefined}
                    rel={openInNewTab ? 'noopener noreferrer' : undefined}
                    className={variant ? cx(classes[variant], className) : className}
                    closeMenuOnClick={isDisabled ? false : undefined}
                    {...itemTestProps}
                  >
                    {name}
                  </Menu.Item>
                ) : (
                  <Menu.Item
                    key={itemIndex}
                    icon={itemIcon}
                    disabled={isDisabled}
                    onClick={onSelect}
                    className={variant ? cx(classes[variant], className) : className}
                    {...itemTestProps}
                  >
                    {name}
                  </Menu.Item>
                );

                if (isDisabled && disabledTooltip) {
                  return (
                    <Tooltip key={itemIndex} label={disabledTooltip} position="right">
                      <div>{item}</div>
                    </Tooltip>
                  );
                }

                return item;
              }
            )}
          </React.Fragment>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

export type MantineDropdownMenuItemVariant = 'destructive';

const useStyles = createStyles((theme) => ({
  destructive: {
    color: theme.colors.red[6],
    '&[data-disabled]': {
      color: theme.colors.red[5],
    },
  },
}));
